/**
 * Edit Reception Dialog — modify a single reception line in-place.
 *
 * Extracted from OrdersDialogs.jsx. Purely presentational; the parent
 * owns `editingReception` state and `onSave` callback.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

export default function EditReceptionDialog({
    open,
    onOpenChange,
    editingReception,
    setEditingReception,
    onSave,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Modifier la réception</DialogTitle>
                    <DialogDescription>
                        {editingReception?.product_name || editingReception?.industrial_product_name || 'Produit'}
                    </DialogDescription>
                </DialogHeader>
                {editingReception && (
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-slate-50 rounded-lg border">
                            <p className="text-sm text-slate-500">Produit</p>
                            <p className="font-medium">{editingReception.product_name || editingReception.industrial_product_name || 'Non défini'}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Date de réception</Label>
                                <Input
                                    type="date"
                                    value={editingReception.date}
                                    onChange={(e) => setEditingReception({ ...editingReception, date: e.target.value })}
                                    data-testid="edit-reception-date"
                                    className="!bg-white !border-2 !border-slate-300 focus-visible:!border-emerald-500"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>N° de pièce (Facture/BL)</Label>
                                <Input
                                    type="text"
                                    placeholder="Ex: FAC-2024-001"
                                    value={editingReception.document_number || ''}
                                    onChange={(e) => setEditingReception({ ...editingReception, document_number: e.target.value })}
                                    data-testid="edit-reception-document"
                                    className="!bg-white !border-2 !border-slate-300 focus-visible:!border-emerald-500"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Quantité reçue</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={editingReception.quantity}
                                    onChange={(e) => setEditingReception({ ...editingReception, quantity: parseInt(e.target.value) || 0 })}
                                    data-testid="edit-reception-quantity"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Prix unitaire (€)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={editingReception.unit_price}
                                    onChange={(e) => setEditingReception({ ...editingReception, unit_price: parseFloat(e.target.value) || 0 })}
                                    data-testid="edit-reception-price"
                                />
                                <label
                                    className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none mt-1"
                                    title="Cocher pour remplacer le prix de référence du produit dans la fiche fournisseur."
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!editingReception.override_supplier_price}
                                        onChange={(e) => setEditingReception({ ...editingReception, override_supplier_price: e.target.checked })}
                                        className="accent-orange-500 h-3 w-3"
                                        data-testid="edit-reception-override-price"
                                    />
                                    MAJ tarif fournisseur
                                </label>
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Annuler
                    </Button>
                    <Button onClick={onSave} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                        Enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
